import { useEffect, useRef } from 'react';

const MAX_SHORTCUTS = 9;

const EDITABLE_SELECTOR = [
  'input',
  'textarea',
  'select',
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[role="textbox"]',
  '[role="combobox"]',
].join(',');

const OVERLAY_SELECTOR = '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]';

function isEditable(element: Element | null) {
  return element instanceof HTMLElement && element.closest(EDITABLE_SELECTOR) !== null;
}

function isInsideOverlay(element: Element | null) {
  return element instanceof HTMLElement && element.closest(OVERLAY_SELECTOR) !== null;
}

type UseAskUserShortcutsParams = {
  /** Vrai uniquement pour la question en attente, interactive. */
  enabled: boolean;
  /** Nombre d'options : les touches 1..count sont écoutées. */
  count: number;
  onSelect: (index: number) => void;
};

/**
 * Touches 1..N pour répondre à la question en attente. Aucun raccourci tant que
 * le focus est dans un champ de saisie (composer compris, même vide : les
 * chiffres restent tapables) ou dans une surcouche (dialogue, menu).
 */
export function useAskUserShortcuts({ count, enabled, onSelect }: UseAskUserShortcutsParams) {
  const onSelectRef = useRef(onSelect);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    if (!enabled || count <= 0) {
      return;
    }

    const limit = Math.min(count, MAX_SHORTCUTS);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented
        || event.repeat
        || event.isComposing
        || event.ctrlKey
        || event.metaKey
        || event.altKey
        || !/^[1-9]$/.test(event.key)
      ) {
        return;
      }

      const index = Number(event.key) - 1;

      if (index >= limit) {
        return;
      }

      const target = event.target instanceof Element ? event.target : null;
      const active = document.activeElement;

      if (isEditable(target) || isEditable(active) || isInsideOverlay(target) || isInsideOverlay(active)) {
        return;
      }

      event.preventDefault();
      onSelectRef.current(index);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [count, enabled]);
}
